import { useRef } from "react";

import { PLAYER_TYPES } from "hooks/usePlayer";
import { jjLogger } from "utils/logUtils";
import { isFunction } from "utils/helperUtils";
import { trackEventVideoRenderedError } from "utils/gAnalyticsUtils";

const useWebGlPlayer = (params) => {
  const transportRef = useRef(null);
  const playerRef = useRef(null);

  const logError = (message) => {
    jjLogger.logError(`WebGL Player Error: ${message}`);
    trackEventVideoRenderedError("WebGL");
  };

  const logDebug = (message) => {
    jjLogger.logDebug(`useWebGlPlayer.js: ${message}`);
  };

  const init = ({ onLoaded, onFailed, onWebGlError, setPlayerType }) => {
    import("webgl-render-library")
      .then((renderLib) => {
        const { SYPlayer, Transport } = renderLib;

        logDebug("initializing SYPlayer");
        try {
          playerRef.current = new SYPlayer(params, () => {
            logError("Context lost. Switching to FMVR");
            if (isFunction(setPlayerType)) setPlayerType(PLAYER_TYPES.HTMLPlayer);
          });

          playerRef.current.addEventListener("loaded", (event) => {
            logDebug("SYPlayer 'loaded' triggered, initializing Transport");
            transportRef.current = new Transport(event.currentTarget);
            if (isFunction(onLoaded)) onLoaded(event);
          });
        } catch (error) {
          if (isFunction(onWebGlError)) onWebGlError(error);
          logError(error.message);
        }
      })
      .catch((error) => {
        if (isFunction(onFailed)) onFailed(error);
        logError(error.message);
      });
  };

  const cleanup = () => {
    if (!playerRef.current) return;

    playerRef.current.removeEventListener("loaded", null);
    playerRef.current.destroy();

    if (transportRef.current) {
      transportRef.current.destroy();
    }

    playerRef.current = null;
    transportRef.current = null;
  };

  const exitFullscreen = () => transportRef.current.closeFullScreen();

  const pausePlayer = () => {
    if (playerRef?.current) {
      playerRef.current.pause();
    }
  };

  const resetPlayer = () => {
    playerRef.current.seekTo(0);
    playerRef.current.pause();
  };

  return { init, cleanup, playerRef, exitFullscreen, pausePlayer, resetPlayer };
};

export default useWebGlPlayer;
